/**
 * Currency Context — single source of truth for the authenticated
 * business's currency formatting metadata (symbol / locale / minor
 * units) plus the set of currencies the platform accepts.
 *
 * Backend resolves this from `business.currency` + the `CURRENCY`
 * catalog in `system_option`. FE hydrates it via
 * `GET /me/currency-context` and caches it (long staleTime, per
 * `businessId` query key) so `formatMoney` never has to hardcode a
 * symbol map that will drift the moment a new currency lands.
 *
 * A new currency lands in three places:
 *   1. Insert a `system_option` row with category='CURRENCY' + the
 *      metadata JSON block matching the shape above.
 *   2. Add the code → symbol pair to `CURRENCY_SYMBOL_MAP` below —
 *      this is the fallback the backend uses when the catalog row
 *      has no explicit `metadata.symbol`.
 *   3. Add the code → locale pair to `CURRENCY_LOCALE_MAP` below —
 *      same fallback story.
 */

export interface CurrencyContextEntry {
  /** ISO 4217 currency code (`DOP`, `USD`, `EUR`, …). */
  code: string;
  /** Display symbol pinned by the backend so every browser renders
   *  identically regardless of `Intl` locale data quirks. */
  symbol: string;
  /** BCP-47 locale used by `Intl.NumberFormat` when formatting an
   *  amount in this currency. */
  locale: string;
  /** Number of minor units (decimals) DGII / accounting expects for
   *  this currency. 2 for DOP / USD / EUR, 0 for CLP / JPY, etc. */
  minorUnits: number;
}

export interface CurrencyContext extends CurrencyContextEntry {
  /** Every currency the platform accepts today. Powers dropdowns
   *  (business-editor, subscription-checkout, cross-currency
   *  reports) without an additional /system-options round-trip. */
  allowed: CurrencyContextEntry[];
}

/**
 * Canonical symbol lookup. Kept in sync with the backend service
 * that populates `CurrencyContext.symbol` — both sides import this
 * constant so a new currency added here propagates to every FE
 * formatter AND every backend PDF / email builder.
 */
export const CURRENCY_SYMBOL_MAP: Record<string, string> = {
  DOP: 'RD$',
  USD: 'US$',
  EUR: '€',
  GBP: '£',
  MXN: 'MX$',
  COP: 'CO$',
  ARS: 'AR$',
  CLP: 'CL$',
  PEN: 'S/',
  BRL: 'R$',
  CAD: 'CA$',
  JPY: '¥',
  CNY: '¥',
  VES: 'Bs.',
  BOB: 'Bs.',
} as const;

/**
 * Canonical locale lookup. Same drift-avoidance story as
 * `CURRENCY_SYMBOL_MAP`.
 */
export const CURRENCY_LOCALE_MAP: Record<string, string> = {
  DOP: 'es-DO',
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  MXN: 'es-MX',
  COP: 'es-CO',
  ARS: 'es-AR',
  CLP: 'es-CL',
  PEN: 'es-PE',
  BRL: 'pt-BR',
  CAD: 'en-CA',
  JPY: 'ja-JP',
  CNY: 'zh-CN',
  VES: 'es-VE',
  BOB: 'es-BO',
} as const;

/** Platform default when a business has no `currency` set yet. */
export const DEFAULT_CURRENCY_CODE = 'DOP';
